import { Label } from '@/components/ui/label';

export function FieldLabel({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
      {required && <span className="ml-1 text-mozaic-red">*</span>}
    </Label>
  );
}

export function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-mozaic-red">{message}</p>;
}

export interface ChipOption<T extends string> {
  value: T;
  label: string;
  color?: string;
}

export function ChipGroup<T extends string>({
  options,
  value,
  onChange,
  wrap,
  allowDeselect,
}: {
  options: readonly ChipOption<T>[];
  value: T | '';
  onChange: (v: T | '') => void;
  wrap?: boolean;
  allowDeselect?: boolean;
}) {
  return (
    <div className={`flex gap-2 ${wrap ? 'flex-wrap' : ''}`}>
      {options.map((opt) => {
        const selected = value === opt.value;
        const bg = selected
          ? opt.color
            ? { backgroundColor: opt.color, borderColor: opt.color, color: 'white' }
            : undefined
          : undefined;
        return (
          <button
            type="button"
            key={opt.value}
            onClick={() => onChange(allowDeselect && selected ? '' : opt.value)}
            style={bg}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              selected && !opt.color
                ? 'border-mozaic-teal bg-mozaic-teal text-white'
                : !selected
                ? 'border-input bg-background hover:bg-muted'
                : ''
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function MultiChipGroup<T extends string>({
  options,
  value,
  onChange,
  wrap,
}: {
  options: readonly ChipOption<T>[];
  value: T[];
  onChange: (v: T[]) => void;
  wrap?: boolean;
}) {
  return (
    <div className={`flex gap-2 ${wrap ? 'flex-wrap' : ''}`}>
      {options.map((opt) => {
        const selected = value.includes(opt.value);
        return (
          <button
            type="button"
            key={opt.value}
            onClick={() =>
              onChange(
                selected ? value.filter((v) => v !== opt.value) : [...value, opt.value],
              )
            }
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              selected
                ? 'border-mozaic-navy bg-mozaic-navy text-white'
                : 'border-input bg-background hover:bg-muted'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function formatDateTimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

export function fromDateTimeLocal(value: string): string {
  // <input type="datetime-local"> emits "YYYY-MM-DDTHH:MM" in local time.
  // Convert to a real ISO string (UTC) so Postgres TIMESTAMPTZ stores it correctly.
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toISOString();
}

export function timeHHMMSS(value: string | null | undefined): string {
  if (!value) return '';
  return value.length >= 5 ? value.slice(0, 5) : value;
}
